import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Box,
  Stack,
  TextField,
  InputAdornment,
  Fade,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Campaign as CampaignIcon,
  Person as PersonIcon
} from "@mui/icons-material";
import { useQuery } from "react-query";
import client from "../../API";
import profilePhoto from "../../Components/globalAssets/ppfLogo.png";

const formatCampaignSum = (sum) => {
  const numericSum = typeof sum === "number" ? sum : parseFloat(sum.replace(/[^\d.]/g, ""));
  return `$${numericSum.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const SelectCampaignDialog = ({ open, onClose, onCampaignSelect }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const { data: campaigns, isLoading } = useQuery(
    'campaigns',
    client.campaigns.list,
    { 
      refetchOnWindowFocus: false,
      enabled: open
    }
  );

  // Filter campaigns based on search term
  const filteredCampaigns = useMemo(() => {
    if (!campaigns) return [];
    if (!searchTerm.trim()) return campaigns;
    
    return campaigns.filter(campaign =>
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.campaign_status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.creators?.[0]?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [campaigns, searchTerm]);

  const statusColorMap = {
    Active: 'success',
    Launched: 'success',
    Pending: 'warning',
    Draft: 'warning',
    Completed: 'info',
    Archived: 'default'
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleCampaignClick = (campaign) => {
    onCampaignSelect(campaign);
    setSearchTerm(""); // Reset search when selecting
  };

  const handleClose = () => {
    setSearchTerm(""); // Reset search when closing
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: 'primary.main', 
          color: "white",
          pb: 2,
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CampaignIcon />
            <Typography variant="h6" component="span">
              Select Campaign
            </Typography>
            <Chip 
              label={`${filteredCampaigns?.length || 0} of ${campaigns?.length || 0}`} 
              size="small"
              sx={{ 
                color: "white", 
                bgcolor: 'primary.dark',
                fontWeight: 500
              }}
            />
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, bgcolor: "#f8fafc" }}>
        {/* Search Bar */}
        <Box sx={{ p: 3, pb: 2, bgcolor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            fullWidth
            placeholder="Search campaigns by name, status, or creator..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={handleClearSearch}
                    sx={{ mr: -1 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  }
                }
              }
            }}
            variant="outlined"
            size="medium"
          />
        </Box>

        {/* Campaign Grid */}
        <Box sx={{ p: 3, minHeight: 400 }}>
          {isLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: 300
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading campaigns...
                </Typography>
              </Box>
            </Box>
          ) : filteredCampaigns.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: 300,
              textAlign: 'center'
            }}>
              <Box>
                <CampaignIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchTerm ? 'No campaigns found' : 'No campaigns available'}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {searchTerm 
                    ? `Try adjusting your search for "${searchTerm}"`
                    : 'Check back later for available campaigns'
                  }
                </Typography>
              </Box>
            </Box>
          ) : (
            <Fade in={!isLoading}>
              <Grid container spacing={2.5}>
                {filteredCampaigns.map((campaign, index) => (
                  <Grid item xs={12} sm={6} lg={4} key={campaign.id}>
                    <Fade in timeout={300 + (index * 50)}>
                      <Card
                        onClick={() => handleCampaignClick(campaign)}
                        sx={{
                          cursor: "pointer",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                            '& .campaign-name': {
                              color: 'primary.main'
                            }
                          },
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          bgcolor: 'white'
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                          {/* Status and Type Chips */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Chip
                              label="Campaign"
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ 
                                fontSize: '0.75rem',
                                height: 24,
                                fontWeight: 500
                              }}
                            />
                            <Chip
                              label={campaign.campaign_status}
                              size="small"
                              color={statusColorMap[campaign.campaign_status] || 'default'}
                              sx={{ 
                                fontSize: '0.75rem',
                                height: 24,
                                fontWeight: 500
                              }}
                            />
                          </Box>

                          {/* Campaign Name */}
                          <Typography 
                            variant="subtitle1" 
                            className="campaign-name"
                            sx={{
                              fontWeight: 600,
                              mb: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              lineHeight: 1.3,
                              minHeight: '2.6em',
                              transition: 'color 0.3s ease'
                            }}
                          >
                            {campaign.name}
                          </Typography>

                          {/* Creator Info */}
                          {campaign.creators?.[0] && (
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: 1.5,
                              p: 1.5,
                              bgcolor: '#f8fafc',
                              borderRadius: 1.5,
                              border: '1px solid #e2e8f0'
                            }}>
                              <Avatar 
                                src={campaign.creators[0]?.pfphref || profilePhoto}
                                sx={{ 
                                  width: 32, 
                                  height: 32,
                                  border: '2px solid white',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {campaign.creators[0]?.name || 'Anonymous Creator'}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}
                                >
                                  <PersonIcon sx={{ fontSize: 12 }} />
                                  Creator
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Fade>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #e2e8f0' }}>
        <Button 
          variant="outlined" 
          onClick={handleClose}
          color="secondary"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectCampaignDialog;